import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseQueryWithReauth } from '../../../utils/baseQueryWithReauth';
import {
  CreateProductData,
  UpdateProductData,
  UpdateProductDescriptionData,
  ProductDetailsResponse, GetParentProductParams, GetParentProductResponse, ProductGridData,
  ProductSearchResponse,
  AuditProfileResponse
} from "../types/types";
import { DeleteProductWebsiteResponse, InsertProductWebsiteRequestBody, ProductWebsiteResponse, UpdateProductWebsiteRequestBody } from "../types/productWebsitesTypes";
import { AssignToAllPriceListsResponse, DeletePriceListItemForProductArgs, DeletePriceListItemForProductResponse, PickPriceListForProductArgs, PickPriceListForProductResponse, ProductPriceListAuditDetailsParams, ProductPriceListAuditDetailsResponse, ProductPriceListResponse, RefreshProductPriceListArgs, RefreshProductPriceListResponse, UpdateProductPriceListItemResponse } from "../types/productPriceListTypes";
import { AssignAllWarehousesToProductResponse, AssignWarehouseToProductArgs, AssignWarehouseToProductResponse, AuditProductWarehouseArgs, AuditProductWarehouseResponse, DeleteWarehouseFromProductArgs, DeleteWarehouseFromProductResponse, ProductWarehouseResponse, UpdateProductWareHouseArgs, UpdateProductWareHouseResponse } from "../types/productWarehouseTypes";
import { ProductImageResponse } from "../types/productImageTypes";
import { AuditProductBundleArgs, AuditProductBundleResponse, DeleteProductBundleArgs, DeleteProductBundleResponse, InsertProductBundleArgs, InsertProductBundleResponse, ProductBundleResponse, UpdateProductBundleRequestBody } from "../types/ProductBundleTypes";
import { ProductConfiguratorFeatureMatrixLabelsResponse, ProductConfiguratorFeatureMatrixValuesResponse } from "../types/ProductConfiguratorTypes";

// Define the argument type for searchProductsByName
export type SearchProductsByNameArgs = {
  orgId: number;
  userId: number;
  searchText: string;
};

export type SearchProductsByIdArgs = {
  orgId: number;
  userId: number;
  productId: number;
};

export type SearchProductsByProductRowIdArgs = {
  orgId: number;
  userId: number;
  productRowId: number;
};

// Add this new type for updating price list items
export type UpdateProductPriceListItemArgs = {
  productRowId: number;
  productPriceListRowId: number;
  body: {
    priceListId?: number;
    productId?: number;
    unitCost?: number;
    unitPurchasePrice?: number;
    unitPrice?: number;
    markupPercent?: number;
    sellRangeMax?: number;
    sellRangeMin?: number;
    oldPrice?: number;
    priceInc?: number;
    defaultTax?: string;
    defaultPurchaseCurrency?: string;
    purchaseTax?: string;
    useVolumePricing?: boolean;
    unitPurchasePriceInc?: number;
    type?: string;
    allowDiscount?: boolean;
    oldPriceInc?: number;
    startingPrice?: number;
  }
};

// Create base query with automatic token refresh
const baseProductQuery = createBaseQueryWithReauth({
  baseUrl: process.env.REACT_APP_UAT_PRODUCT_API_URL || '',
});


export const productApiSlice = createApi({
  reducerPath: 'productApi',
  baseQuery: baseProductQuery,
  endpoints: (builder) => ({

    //search products by name
    searchProductsByName: builder.query<ProductSearchResponse, SearchProductsByNameArgs>({
      query: (data) => ({
        url: `/Search/ProductName/${encodeURIComponent(data.searchText)}`,
        method: 'GET',
      }),
    }),

    //search product by id
    SearchProductById: builder.query<ProductSearchResponse, SearchProductsByIdArgs>({
      query: (data) => ({
        url: `/Search/ProductId/${data.productId}`,
        method: 'GET',
      }),
    }),

    //search product by row id
    SearchByProductRowId: builder.query<ProductSearchResponse, SearchProductsByProductRowIdArgs>({
      query: (data) => ({
        url: `/Search/RowId/${data.productRowId}`,
        method: 'GET',
      }),
    }),

    //search product by product code
    SearchByProductCode: builder.query<ProductSearchResponse, { productCode: string }>({
      query: (data) => ({
        url: `/Search/ProductCode/${encodeURIComponent(data.productCode)}`,
        method: 'GET',
      }),
    }),

    //get product details
    getProductDetails: builder.query<ProductDetailsResponse, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}`,
        method: 'GET',
      }),
    }),

    //create product
    createProduct: builder.mutation<ProductGridData, CreateProductData>({
      query: (data) => ({
        url: `/`,
        method: 'POST',
        body: data,
      }),
    }),

    //update product
    updateProduct: builder.mutation<ProductGridData, UpdateProductData>({
      query: (data) => ({
        url: `/${data.rowId}`,
        method: 'PUT',
        body: data,
      }),
    }),

    //delete product
    deleteProduct: builder.mutation<ProductGridData, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}`,
        method: 'DELETE',
      }),
    }),

    //get product description 
    getProductDescription: builder.query<ProductGridData, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductDescriptions`,
        method: 'GET',
      }),
    }),

    //update product description
    updateProductDescription: builder.mutation<ProductGridData, UpdateProductDescriptionData>({
      query: (data) => ({
        url: `/UpdateProductDescription`,
        method: 'PUT',
        body: data,
      }),
    }),

    //get product margins
    getProductMargins: builder.query<ProductGridData, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductMargins`,
        method: 'GET',
      }),
    }),

    //get parent product
    getParentProduct: builder.query<GetParentProductResponse, GetParentProductParams>({
      query: (data) => ({
        url: `/ParentProducts?locId=${data.locId}&locIdTo=${data.locIdTo}&priceListId=${data.priceListId}&productName=${encodeURIComponent(data.productName)}&productCode=${encodeURIComponent(data.productCode)}&category=${encodeURIComponent(data.category)}&vendor=${encodeURIComponent(data.vendor)}&barcode=${encodeURIComponent(data.barcode)}&orderable=${data.orderable}`,
        method: 'GET',
      }),
    }),

    //get Product Images
    getProductImages: builder.query<ProductGridData, { productRowId: number }>({
      query: (data) => ({
        url: `/Images`,
        method: 'GET',
      }),
    }),

    //get audit profile 
    getAuditProfile: builder.query<AuditProfileResponse, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}/Audit`,
        method: 'GET',
      }),
    }),

    //--------------------- Product Website APIs ---------------------

    //get product website
    getProductWebsite: builder.query<ProductWebsiteResponse, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductWebsite`,
        method: 'GET',
      }),
    }),

    //update product website
    updateProductWebsite: builder.mutation<ProductWebsiteResponse, { productRowId: number, body: UpdateProductWebsiteRequestBody }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductWebsite/${data.body.rowId}`,
        method: 'PUT',
        body: data.body.body,
      }),
    }),

    //delete product website 
    deleteProductWebsite: builder.mutation<DeleteProductWebsiteResponse, { productRowId: number, webSiteId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductWebsite/${data.webSiteId}`,
        method: 'DELETE',
      }),
    }),

    //assign product website
    assignProductWebsite: builder.mutation<ProductWebsiteResponse, { productRowId: number, body: InsertProductWebsiteRequestBody }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductWebsite/${data.body.websiteId}/Assign`,
        method: 'POST',
        body: data.body,
      }),
    }),


    //--------------------- Product Price List APIs ---------------------

    //get product price list
    getProductPriceList: builder.query<ProductPriceListResponse, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductPriceList`,
        method: 'GET',
      }),
    }),

    //update product price list item
    updateProductPriceListItem: builder.mutation<UpdateProductPriceListItemResponse, UpdateProductPriceListItemArgs>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductPriceList/${data.productPriceListRowId}`,
        method: 'PUT',
        body: data.body,
      }),
    }),

    //pick price list item for product
    pickPriceListForProduct: builder.mutation<PickPriceListForProductResponse, PickPriceListForProductArgs>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductPriceList`,
        method: 'POST',
        body: data.body,
      }),
    }),

    //get product price list audit details
    getProductPriceListAuditDetails: builder.query<ProductPriceListAuditDetailsResponse, ProductPriceListAuditDetailsParams>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductPriceList/${data.productPriceListRowId}/Audit`,
        method: 'GET',
      }),
    }),

    //Assign to All price lists
    assignToAllPriceLists: builder.mutation<AssignToAllPriceListsResponse, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductPriceList/AssignAll`,
        method: 'POST',
      }),
    }),

    //Delete price list item for product
    deletePriceListItemForProduct: builder.mutation<DeletePriceListItemForProductResponse, DeletePriceListItemForProductArgs>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductPriceList/${data.productPriceListRowId}`,
        method: 'DELETE',
      }),
    }),

    //Refresh product price list
    refreshProductPriceList: builder.mutation<RefreshProductPriceListResponse, RefreshProductPriceListArgs>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductPriceList/RefreshPrice`,
        method: 'POST',
        body: data.body,
      }),
    }),

    //--------------------- Product Warehouse APIs ---------------------

    //get product warehouse
    getProductWarehouse: builder.query<ProductWarehouseResponse, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductWarehouse`,
        method: 'GET',
      }),
    }),


    //Assign warehouse to product
    assignWarehouseToProduct: builder.mutation<AssignWarehouseToProductResponse, AssignWarehouseToProductArgs>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductWarehouse/${data.locationRowId}/Assign`,
        method: 'POST',
      }),
    }),


    // Update product warehouse
    updateProductWarehouse: builder.mutation<UpdateProductWareHouseResponse, UpdateProductWareHouseArgs>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductWarehouse/${data.warehouseRowId}`,
        method: 'PUT',
        body: data.body,
      }),
    }),

    //Assign all warehouses to product
    assignAllWarehousesToProduct: builder.mutation<AssignAllWarehousesToProductResponse, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductWarehouse/AssignAll`,
        method: 'POST',
      }),
    }),

    //delete warehouse from product
    deleteWarehouseFromProduct: builder.mutation<DeleteWarehouseFromProductResponse, DeleteWarehouseFromProductArgs>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductWarehouse/${data.warehouseRowId}`,
        method: 'DELETE',
      }),
    }),

    //check for concurrent updates
    auditProductWarehouseDetails: builder.query<AuditProductWarehouseResponse, AuditProductWarehouseArgs>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductWarehouse/${data.warehouseRowId}/Audit`,
        method: 'GET',
      }),
    }),


    //--------------------- Product Image APIs ---------------------

    //get product image
    getProductImage: builder.query<ProductImageResponse, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductImage`,
        method: 'GET',
      }),
    }),


    //--------------------- Product Bundle APIs ---------------------
    getProductBundle: builder.query<ProductBundleResponse, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductBundle`,
        method: 'GET',
      }),
    }),

    updateProductBundle: builder.mutation<ProductBundleResponse, { productRowId: number, body: UpdateProductBundleRequestBody }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductBundle`,
        method: 'PUT',
        body: data.body,
      }),
    }),

    // Audit product bundle
    auditProductBundle: builder.query<AuditProductBundleResponse, AuditProductBundleArgs>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductBundle/${data.productBundleRowId}/Audit`,
        method: 'GET',
      }),
    }),

    // Delete Product Bundle
    deleteProductBundle: builder.mutation<DeleteProductBundleResponse, DeleteProductBundleArgs>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductBundle/${data.productBundleRowId}`,
        method: 'DELETE',
      }),
    }),

    // Insert Product Bundle
    insertProductBundle: builder.mutation<InsertProductBundleResponse, InsertProductBundleArgs>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductBundle/InsertProductBundle`,
        method: 'POST',
        body: data.body,
      }),
    }),

    //--------------------- Product Configurator APIs ---------------------

    //Get Feature Matrix Labels
    getProductConfiguratorFeatureMatrixLabels: builder.query<ProductConfiguratorFeatureMatrixLabelsResponse, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductConfigurator/FeatureMatrixLabels`,
        method: 'GET',
      }),
    }),

    //Get Product Configurator Feature Matrix Values
    getProductConfiguratorFeatureMatrixValues: builder.query<ProductConfiguratorFeatureMatrixValuesResponse, { productRowId: number }>({
      query: (data) => ({
        url: `/${data.productRowId}/ProductConfigurator/FeatureMatrixValues`,
        method: 'GET',
      }),
    }),
  })
});

export const {
  useSearchProductsByNameQuery,
  useSearchProductByIdQuery,
  useSearchByProductRowIdQuery,
  useSearchByProductCodeQuery,
  useGetProductDetailsQuery,
  useLazySearchProductsByNameQuery,
  useLazySearchProductByIdQuery,
  useLazySearchByProductRowIdQuery,
  useLazySearchByProductCodeQuery,
  useLazyGetProductDetailsQuery,
  useUpdateProductMutation,
  useGetParentProductQuery,
  useLazyGetParentProductQuery,
  useCreateProductMutation,
  useDeleteProductMutation,
  useUpdateProductDescriptionMutation,
  useGetProductMarginsQuery,
  useLazyGetProductMarginsQuery,
  useGetAuditProfileQuery,
  useLazyGetAuditProfileQuery,
  
  //Image APIs
  useLazyGetProductDescriptionQuery,
  useGetProductImagesQuery,
  useLazyGetProductImagesQuery,
  
  //Website APIs
  useGetProductWebsiteQuery,
  useLazyGetProductWebsiteQuery,
  useUpdateProductWebsiteMutation,
  useDeleteProductWebsiteMutation,
  useAssignProductWebsiteMutation,

  //Image APIs
  useGetProductImageQuery,
  useLazyGetProductImageQuery,

  //Price List APIs
  useGetProductPriceListQuery,
  useLazyGetProductPriceListQuery,
  useUpdateProductPriceListItemMutation,
  usePickPriceListForProductMutation,
  useGetProductPriceListAuditDetailsQuery,
  useLazyGetProductPriceListAuditDetailsQuery,
  useAssignToAllPriceListsMutation,
  useDeletePriceListItemForProductMutation,
  useRefreshProductPriceListMutation,

  //Warehouse APIs
  useGetProductWarehouseQuery,
  useLazyGetProductWarehouseQuery,
  useAssignWarehouseToProductMutation,
  useUpdateProductWarehouseMutation,
  useAuditProductWarehouseDetailsQuery,
  useLazyAuditProductWarehouseDetailsQuery,
  useAssignAllWarehousesToProductMutation,
  useDeleteWarehouseFromProductMutation,

  //Bundle APIs
  useGetProductBundleQuery,
  useLazyGetProductBundleQuery,
  useUpdateProductBundleMutation,
  useAuditProductBundleQuery,
  useLazyAuditProductBundleQuery,
  useDeleteProductBundleMutation,
  useInsertProductBundleMutation,

  //Configurator APIs
  useGetProductConfiguratorFeatureMatrixLabelsQuery,
  useLazyGetProductConfiguratorFeatureMatrixLabelsQuery,
  useGetProductConfiguratorFeatureMatrixValuesQuery,
  useLazyGetProductConfiguratorFeatureMatrixValuesQuery,
} = productApiSlice;